import React, { Suspense, useRef } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import * as THREE from "three";

const TARGET_HEIGHT = 2.0;

function SkullMesh() {
  const group = useRef();
  const obj = useLoader(OBJLoader, "/models/skull.obj");

  const { cloned, scale, center } = React.useMemo(() => {
    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color("#d4c4a0"),
      roughness: 0.55,
      metalness: 0.04,
      clearcoat: 0.2,
      clearcoatRoughness: 0.55,
      sheen: 0.25,
      sheenColor: new THREE.Color("#6a5a3c"),
      envMapIntensity: 1.0,
      emissive: new THREE.Color("#2a1d0c"),
      emissiveIntensity: 0.08,
      side: THREE.DoubleSide,
    });

    const c = obj.clone(true);
    c.traverse((child) => {
      if (child.isMesh) {
        child.material = mat;
        if (child.geometry && !child.geometry.attributes.normal) {
          child.geometry.computeVertexNormals();
        }
      }
    });
    c.rotation.x = -Math.PI / 2;
    c.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(c);
    const size = new THREE.Vector3();
    const cen = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(cen);

    return {
      cloned: c,
      scale: TARGET_HEIGHT / size.y,
      center: cen,
    };
  }, [obj]);

  useFrame((state) => {
    if (group.current) {
      group.current.rotation.y = state.clock.elapsedTime * 0.45;
      group.current.rotation.x =
        Math.sin(state.clock.elapsedTime * 0.4) * 0.12;
    }
  });

  const setRef = (node) => {
    if (node && cloned.parent !== node) {
      while (node.children.length) node.remove(node.children[0]);
      node.add(cloned);
    }
  };

  return (
    <group ref={group}>
      <group
        ref={setRef}
        position={[-center.x * scale, -center.y * scale, -center.z * scale]}
        scale={[scale, scale, scale]}
      />
    </group>
  );
}

export default function SkullLogo({ size = 110 }) {
  // Defer Canvas mount one tick to avoid React.StrictMode double-mount creating
  // a stale WebGL context on the same canvas DOM node.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setMounted(true), 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="relative"
      style={{
        width: size,
        height: size,
      }}
      aria-hidden
    >
      {/* gold halo behind */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(200,161,74,0.28), transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      {mounted && (
        <Canvas
          dpr={[1, 1.6]}
          camera={{ position: [0, 0.1, 3.4], fov: 32 }}
          gl={{ antialias: true, alpha: true, preserveDrawingBuffer: false }}
          frameloop="always"
          style={{ width: "100%", height: "100%" }}
        >
          <ambientLight intensity={0.45} />
          <directionalLight position={[3, 4, 3]} intensity={1.3} color="#f1c879" />
          <directionalLight position={[-3, 1, -2]} intensity={0.6} color="#3a8e74" />
          <pointLight position={[-1.5, 1, 2]} intensity={0.8} color="#e0b46a" />
          <Suspense fallback={null}>
            <Environment preset="night" />
            <SkullMesh />
          </Suspense>
        </Canvas>
      )}
    </div>
  );
}
